import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Button,
  Tag,
  message,
  Space,
  Avatar,
  Spin,
  Typography,
  Empty,
  Alert,
  ConfigProvider,
  Timeline,
  Switch,
  Popconfirm,
  Input,
  Radio,
  Tooltip,
} from 'antd';
import {
  ArrowRightOutlined,
  PhoneOutlined,
  SoundOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  DeleteOutlined,
  CopyOutlined,
  CloudDownloadOutlined,
  EyeOutlined,
  AudioOutlined,
  ApiOutlined,
  FileDoneOutlined,
} from '@ant-design/icons';
import { callCenterCallsApi } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import { DetailPageSkeleton } from '../../components/SkeletonLoaders';
import { toJalali } from '../../utils/dateUtils';

const { Text, Paragraph } = Typography;

const STATUS_META = {
  none: { label: 'بدون رونویسی', color: 'default', icon: <ClockCircleOutlined /> },
  pending: { label: 'در صف انتظار', color: 'orange', icon: <ClockCircleOutlined /> },
  processing: { label: 'در حال پردازش', color: 'processing', icon: <LoadingOutlined /> },
  completed: { label: 'رونویسی موفق', color: 'success', icon: <CheckCircleOutlined /> },
  failed: { label: 'خطا در رونویسی', color: 'error', icon: <CloseCircleOutlined /> },
};

const LEVEL_META = {
  info: { label: 'اطلاعات', color: 'blue', icon: <InfoCircleOutlined /> },
  success: { label: 'موفق', color: 'success', icon: <CheckCircleOutlined /> },
  warning: { label: 'هشدار', color: 'warning', icon: <ExclamationCircleOutlined /> },
  error: { label: 'خطا', color: 'error', icon: <CloseCircleOutlined /> },
};

const STEP_LABELS = {
  queued: 'ثبت در صف رونویسی',
  auto_queued: 'ثبت خودکار در صف',
  started: 'شروع پردازش جاب',
  settings_verified: 'تایید تنظیمات و توکن',
  settings_missing: 'خطای فقدان تنظیمات',
  status_processing: 'تغییر وضعیت به در حال پردازش',
  audio_search_local: 'جستجوی صوت محلی',
  audio_found_local: 'فایل صوتی محلی یافت شد',
  audio_search_cloud: 'جستجو در فضای ابری MinIO',
  audio_downloaded_cloud: 'دریافت از فضای ابری',
  audio_cloud_error: 'خطای حافظه ابری',
  audio_search_remote: 'بررسی آدرس دانلود ریموت',
  audio_downloaded_remote: 'دانلود از آدرس خارجی',
  audio_remote_error: 'خطای دانلود آدرس خارجی',
  audio_missing: 'عدم وجود فایل صوتی',
  audio_read_error: 'خطای خواندن فایل صوتی',
  api_request_prepared: 'آماده‌سازی فایل ارسالی',
  api_sending: 'ارسال به سرور هوش مصنوعی',
  completed: 'تکمیل موفقیت‌آمیز رونویسی',
  api_no_segments: 'عدم وجود متن در پاسخ سرور',
  api_http_error: 'خطای HTTP سرور رونویسی',
  job_exception: 'خطای سیستمی در پردازش',
  failed: 'شکست نهایی فرآیند',
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  const s = Number(seconds) || 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const CallTranscribeLogs = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [call, setCall] = useState(null);
  const [logs, setLogs] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [summary, setSummary] = useState(null);

  const [selectedAttempt, setSelectedAttempt] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [expandedContextIds, setExpandedContextIds] = useState({});

  const pollingTimerRef = useRef(null);

  const fetchLogs = useCallback(
    async (isBackground = false) => {
      if (!isBackground) {
        setRefreshing(true);
      }
      try {
        const res = await callCenterCallsApi.getTranscribeLogs(id);
        const data = res?.data || {};
        setCall(data.call || null);
        setLogs(Array.isArray(data.logs) ? data.logs : []);
        setAttempts(Array.isArray(data.attempts) ? data.attempts : []);
        setSummary(data.summary || null);
      } catch (error) {
        if (!isBackground) {
          message.error(error?.message || 'خطا در دریافت لاگ‌های رونویسی تماس');
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [id],
  );

  useEffect(() => {
    fetchLogs(false);
  }, [fetchLogs]);

  // پولینگ زنده هنگام پردازش یا فعال بودن سوئیچ
  useEffect(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }

    const isProcessing =
      call?.transcribe_status === 'pending' || call?.transcribe_status === 'processing';

    if (autoRefresh || isProcessing) {
      pollingTimerRef.current = setInterval(() => {
        fetchLogs(true);
      }, 3000);
    }

    return () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
      }
    };
  }, [autoRefresh, call?.transcribe_status, fetchLogs]);

  const handleRetryTranscribe = async () => {
    setRetrying(true);
    try {
      const res = await callCenterCallsApi.transcribe(id);
      message.success(res?.message || 'درخواست رونویسی با موفقیت در صف قرار گرفت.');
      setAutoRefresh(true);
      await fetchLogs(false);
    } catch (error) {
      message.error(error?.message || 'خطا در ارسال درخواست رونویسی');
    } finally {
      setRetrying(false);
    }
  };

  const handleClearLogs = async () => {
    setClearing(true);
    try {
      const res = await callCenterCallsApi.clearTranscribeLogs(id);
      message.success(res?.message || 'لاگ‌های رونویسی با موفقیت پاک شدند.');
      await fetchLogs(false);
    } catch (error) {
      message.error(error?.message || 'خطا در پاکسازی لاگ‌ها');
    } finally {
      setClearing(false);
    }
  };

  const toggleExpandContext = (logId) => {
    setExpandedContextIds((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  const copyToClipboard = (text, msg = 'در کلیپ‌بورد کپی شد') => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      message.success(msg);
    }
  };

  // فیلتر کردن لاگ‌ها بر اساس attempt، سطح و جستجو
  const filteredLogs = useMemo(() => {
    return logs.filter((l) => {
      if (selectedAttempt !== 'all' && l.attempt_id !== selectedAttempt) {
        return false;
      }
      if (levelFilter !== 'all' && l.level !== levelFilter) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const matchMsg = (l.message || '').toLowerCase().includes(q);
        const matchStep = (l.step || '').toLowerCase().includes(q);
        const matchContext = l.context ? JSON.stringify(l.context).toLowerCase().includes(q) : false;
        if (!matchMsg && !matchStep && !matchContext) {
          return false;
        }
      }
      return true;
    });
  }, [logs, selectedAttempt, levelFilter, searchQuery]);

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 custom-font">
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  const currentStatus = call?.transcribe_status || (call?.is_transcribed ? 'completed' : 'none');
  const statusMeta = STATUS_META[currentStatus] || STATUS_META.none;
  const isProcessing = currentStatus === 'pending' || currentStatus === 'processing';

  return (
    <MainLayout>
      <ConfigProvider theme={{ token: { fontFamily: 'CustomFont, sans-serif' } }}>
        <div className="custom-font space-y-6 max-w-full overflow-x-hidden p-2 sm:p-4 md:p-6" style={{ minWidth: 0 }}>
          
          {/* نوار بالایی هدر و اکشن‌ها */}
          <Card className="shadow-md rounded-2xl border-0 overflow-hidden" bodyStyle={{ padding: '16px 24px' }}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Button
                  type="text"
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate(`/call-center/calls/${id}`)}
                  className="custom-font"
                />
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-600 text-white flex items-center justify-center shadow-lg">
                  <AudioOutlined className="text-xl" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 m-0 custom-font">
                      لاگ‌ها و فرآیند رونویسی تماس #{id}
                    </h1>
                    <Tag color={statusMeta.color} icon={statusMeta.icon} className="custom-font m-0 text-sm py-0.5 px-2.5">
                      {statusMeta.label}
                    </Tag>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                    <span>ثبت‌شده در دیتابیس مرکز تماس</span>
                    {call?.started_at && (
                      <>
                        <span>•</span>
                        <span>{toJalali(call.started_at, 'jYYYY/jMM/jDD HH:mm:ss')}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* دکمه‌های اقدام */}
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 py-1 px-2.5 rounded-lg text-xs text-gray-600 dark:text-gray-300">
                  <span>بروزرسانی خودکار</span>
                  <Switch
                    size="small"
                    checked={autoRefresh}
                    onChange={(checked) => setAutoRefresh(checked)}
                  />
                </div>

                <Button
                  icon={<ReloadOutlined spin={refreshing} />}
                  onClick={() => fetchLogs(false)}
                  disabled={refreshing}
                  className="custom-font"
                >
                  بروزرسانی
                </Button>

                <Button
                  type="primary"
                  icon={<AudioOutlined />}
                  onClick={handleRetryTranscribe}
                  loading={retrying}
                  disabled={isProcessing}
                  className="custom-font bg-purple-600 hover:bg-purple-700 border-none"
                >
                  {currentStatus === 'failed' ? 'تلاش مجدد رونویسی' : 'اجرای رونویسی'}
                </Button>

                <Button
                  icon={<EyeOutlined />}
                  onClick={() => navigate(`/call-center/calls/${id}`)}
                  className="custom-font"
                >
                  جزئیات تماس
                </Button>

                {logs.length > 0 && (
                  <Popconfirm
                    title="پاکسازی لاگ‌های رونویسی"
                    description="آیا از پاک کردن تمامی لاگ‌های ثبت‌شده برای این تماس اطمینان دارید؟"
                    okText="بله، پاک شود"
                    cancelText="انصراف"
                    okButtonProps={{ danger: true }}
                    onConfirm={handleClearLogs}
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      loading={clearing}
                      className="custom-font"
                    >
                      پاکسازی لاگ‌ها
                    </Button>
                  </Popconfirm>
                )}
              </div>
            </div>
          </Card>

          {/* خلاصه تماس و پلیر صوت */}
          {call && (
            <Card className="shadow-md rounded-2xl border-0 overflow-hidden" bodyStyle={{ padding: 20 }}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                <div className="flex items-center gap-3">
                  <Avatar
                    src={getImagePath(call.agent?.image)}
                    size={48}
                    className="bg-indigo-600 text-white font-bold"
                  >
                    {call.agent?.name?.charAt(0) || <UserOutlined />}
                  </Avatar>
                  <div>
                    <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
                      {call.agent?.name || 'اپراتور نامشخص'}
                    </div>
                    <div className="text-xs text-gray-500">
                      داخلی: <span dir="ltr">{call.extension || '—'}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-xs text-gray-400">شماره مخاطب</div>
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-200" dir="ltr">
                    {call.phone || '—'}
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-xs text-gray-400">مدت مکالمه</div>
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-200" dir="ltr">
                    {formatDuration(call.billsec ?? call.duration)}
                  </div>
                </div>

                {/* پلیر صوت اگر موجود باشد */}
                <div className="md:col-span-1">
                  {call.audio_url || call.audio_file ? (
                    <div className="bg-gray-50 dark:bg-gray-800/80 p-2 rounded-xl flex items-center gap-2">
                      <SoundOutlined className="text-purple-600 text-lg" />
                      <audio
                        controls
                        src={call.audio_url}
                        className="w-full h-8"
                        style={{ maxHeight: 32 }}
                        preload="metadata"
                      />
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic">بدون فایل صوتی ضبط‌شده</div>
                  )}
                </div>
              </div>

              {/* هشدار خطا اگر وجود داشته باشد */}
              {call.transcribe_error && (
                <Alert
                  type="error"
                  showIcon
                  className="mt-4 rounded-xl custom-font text-sm"
                  message="علت آخرین خطای رونویسی:"
                  description={<span className="font-mono text-xs">{call.transcribe_error}</span>}
                />
              )}

              {/* هشدار غیرفعال بودن ذخیره لاگ در تنظیمات */}
              {summary && summary.logging_enabled === false && (
                <Alert
                  type="warning"
                  showIcon
                  className="mt-4 rounded-xl custom-font text-xs"
                  message="ثبت لاگ رونویسی در پایگاه‌داده غیرفعال است"
                  description={
                    <span>
                      گزینه ذخیره‌سازی لاگ‌های رونویسی در تنظیمات مرکز تماس غیرفعال شده است. در صورت نیاز می‌توانید آن را از منوی{' '}
                      <Link to="/call-center/settings" className="font-bold underline text-purple-600">
                        تنظیمات مرکز تماس (تب رونویسی)
                      </Link>{' '}
                      مجدداً فعال کنید.
                    </span>
                  }
                />
              )}
            </Card>
          )}

          {/* فیلترها و آمار تلاش‌ها */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              {/* فیلتر Attempts */}
              {attempts.length > 1 && (
                <Radio.Group
                  value={selectedAttempt}
                  onChange={(e) => setSelectedAttempt(e.target.value)}
                  buttonStyle="solid"
                  size="middle"
                  className="custom-font"
                >
                  <Radio.Button value="all">همه تلاش‌ها ({logs.length})</Radio.Button>
                  {attempts.map((att, idx) => (
                    <Radio.Button key={att.attempt_id} value={att.attempt_id}>
                      تلاش {attempts.length - idx}
                      {att.status === 'completed' && <CheckCircleOutlined className="text-green-500 mr-1.5" />}
                      {att.status === 'failed' && <CloseCircleOutlined className="text-red-500 mr-1.5" />}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              )}

              {/* فیلتر سطح لاگ */}
              <Radio.Group
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                size="middle"
                className="custom-font"
              >
                <Radio.Button value="all">همه سطوح</Radio.Button>
                <Radio.Button value="error">فقط خطاها</Radio.Button>
                <Radio.Button value="success">موفق</Radio.Button>
                <Radio.Button value="info">اطلاعات</Radio.Button>
              </Radio.Group>
            </div>

            <div className="w-full md:w-72">
              <Input
                placeholder="جستجو در لاگ‌ها..."
                allowClear
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rounded-xl custom-font"
              />
            </div>
          </div>

          {/* خط زمان فرآیند رونویسی */}
          <Card
            className="shadow-md rounded-2xl border-0 overflow-hidden"
            title={
              <span className="custom-font font-bold flex items-center gap-2 text-base">
                <ApiOutlined className="text-purple-600" />
                رویدادها و گزارش فرآیند رونویسی در پایگاه‌داده ({filteredLogs.length} رویداد)
              </span>
            }
            extra={
              logs.length > 0 && (
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() =>
                    copyToClipboard(
                      JSON.stringify(logs, null, 2),
                      'تمام لاگ‌ها به صورت JSON کپی شدند.',
                    )
                  }
                  className="custom-font"
                >
                  کپی تمام لاگ‌ها (JSON)
                </Button>
              )
            }
          >
            {filteredLogs.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div className="custom-font text-gray-500 py-6">
                    {logs.length === 0
                      ? 'هنوز هیچ لاگی برای رونویسی این تماس ثبت نشده است. با کلیک بر روی دکمه «اجرای رونویسی»، فرآیند را شروع کنید.'
                      : 'موردی با فیلترهای انتخابی یافت نشد.'}
                  </div>
                }
              />
            ) : (
              <div className="py-4 px-2 sm:px-6">
                <Timeline
                  mode="left"
                  items={filteredLogs.map((logItem) => {
                    const lMeta = LEVEL_META[logItem.level] || LEVEL_META.info;
                    const stepTitle = STEP_LABELS[logItem.step] || logItem.step;
                    const hasContext = logItem.context && Object.keys(logItem.context).length > 0;
                    const isExpanded = Boolean(expandedContextIds[logItem.id]);

                    return {
                      key: logItem.id,
                      color:
                        logItem.level === 'error'
                          ? 'red'
                          : logItem.level === 'success'
                          ? 'green'
                          : logItem.level === 'warning'
                          ? 'gold'
                          : 'blue',
                      dot: (
                        <span className="text-base">
                          {logItem.level === 'error' ? (
                            <CloseCircleOutlined className="text-red-500" />
                          ) : logItem.level === 'success' ? (
                            <CheckCircleOutlined className="text-green-500" />
                          ) : logItem.level === 'warning' ? (
                            <ExclamationCircleOutlined className="text-yellow-500" />
                          ) : (
                            <InfoCircleOutlined className="text-blue-500" />
                          )}
                        </span>
                      ),
                      children: (
                        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/60 rounded-xl p-3.5 mb-2 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Tag color={lMeta.color} className="custom-font m-0 text-xs font-semibold">
                                {stepTitle}
                              </Tag>
                              <span className="font-mono text-xs text-gray-400" dir="ltr">
                                [{logItem.step}]
                              </span>
                            </div>
                            <span className="text-xs text-gray-400 font-mono" dir="ltr">
                              {logItem.created_at
                                ? toJalali(logItem.created_at, 'jYYYY/jMM/jDD HH:mm:ss') ||
                                  logItem.created_at
                                : '—'}
                            </span>
                          </div>

                          <div className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-2">
                            {logItem.message}
                          </div>

                          {/* نمایش جزئیات و پارامترها (Context) */}
                          {hasContext && (
                            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/50">
                              <div className="flex items-center justify-between">
                                <Button
                                  type="link"
                                  size="small"
                                  className="p-0 text-xs custom-font text-purple-600 hover:text-purple-700"
                                  onClick={() => toggleExpandContext(logItem.id)}
                                >
                                  {isExpanded ? 'بستن جزئیات فنی ▲' : 'مشاهده جزئیات فنی و پارامترها ▼'}
                                </Button>
                                {isExpanded && (
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined />}
                                    className="text-xs text-gray-400"
                                    onClick={() =>
                                      copyToClipboard(
                                        JSON.stringify(logItem.context, null, 2),
                                        'پارامترها کپی شد',
                                      )
                                    }
                                  >
                                    کپی
                                  </Button>
                                )}
                              </div>

                              {isExpanded && (
                                <pre
                                  dir="ltr"
                                  className="mt-2 p-3 bg-gray-900 text-green-400 rounded-lg text-xs overflow-x-auto font-mono max-h-60"
                                >
                                  {JSON.stringify(logItem.context, null, 2)}
                                </pre>
                              )}
                            </div>
                          )}
                        </div>
                      ),
                    };
                  })}
                />
              </div>
            )}
          </Card>

          {/* پیش‌نمایش متن رونویسی شده مکالمه اگر تکمیل شده باشد */}
          {call?.transcribed_segments && call.transcribed_segments.length > 0 && (
            <Card
              className="shadow-md rounded-2xl border-0 overflow-hidden"
              title={
                <span className="custom-font font-bold flex items-center gap-2 text-base">
                  <FileDoneOutlined className="text-green-600" />
                  متن استخراج‌شده از صوت ({call.transcribed_segments.length} قطعه)
                </span>
              }
              extra={
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    const fullText = call.transcribed_segments
                      .map((s) => `${s.speaker_id === 1 ? 'اپراتور' : 'مخاطب'}: ${s.text}`)
                      .join('\n');
                    copyToClipboard(fullText, 'متن رونویسی کپی شد.');
                  }}
                  className="custom-font"
                >
                  کپی کل متن
                </Button>
              }
            >
              <div className="space-y-3 p-2 sm:p-4">
                {call.transcribed_segments.map((seg, idx) => {
                  const isAgent = seg.speaker_id === 1;
                  return (
                    <div
                      key={idx}
                      className={`flex flex-col ${isAgent ? 'items-start' : 'items-end'}`}
                    >
                      <div
                        className={`p-3.5 rounded-2xl max-w-2xl shadow-sm ${
                          isAgent
                            ? 'bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-tr-none'
                            : 'bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-tl-none'
                        }`}
                      >
                        <div
                          className={`flex items-center gap-2 mb-1.5 ${
                            isAgent ? 'justify-start' : 'justify-end'
                          }`}
                        >
                          <span className="font-bold text-xs text-gray-700 dark:text-gray-200">
                            {isAgent ? 'اپراتور' : 'مخاطب'}
                          </span>
                          {seg.time_range && (
                            <span className="text-gray-400 text-xs font-mono" dir="ltr">
                              {seg.time_range}
                            </span>
                          )}
                          {seg.emotion && (
                            <Tag color="cyan" className="text-xs m-0">
                              {seg.emotion}
                            </Tag>
                          )}
                        </div>
                        <div className="text-sm text-gray-800 dark:text-gray-100 leading-relaxed">
                          {seg.text}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

        </div>
      </ConfigProvider>
    </MainLayout>
  );
};

export default CallTranscribeLogs;
